import { useMemo } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/ui/accordion";
import { Download } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type { ReportGalleryItem } from "./types";

interface ReportTreeNavProps {
  items: ReportGalleryItem[];
  selectedItemId: string | null;
  expanded: Set<string>;
  onToggleCategory: (category: string) => void;
  onSelectItem: (itemId: string) => void;
  onDownloadCategory?: (category: string, itemsInCategory: ReportGalleryItem[]) => void;
  query: string;
  emptyMessage: string;
  showItemsWithoutPhotos?: boolean;
}

export function ReportTreeNav({
  items,
  selectedItemId,
  expanded,
  onToggleCategory,
  onSelectItem,
  onDownloadCategory,
  query,
  emptyMessage,
  showItemsWithoutPhotos = false,
}: ReportTreeNavProps) {
  const q = query.trim().toLowerCase();

  const grouped = useMemo(() => {
    const byCat = new Map<string, ReportGalleryItem[]>();
    for (const item of items) {
      if (!showItemsWithoutPhotos && item.photos.length === 0) continue;
      if (q && !item.name.toLowerCase().includes(q)) continue;
      const list = byCat.get(item.category) ?? [];
      list.push(item);
      byCat.set(item.category, list);
    }
    return Array.from(byCat.entries())
      .map(([category, categoryItems]) => ({
        category,
        order: Math.min(...categoryItems.map((item) => item.categoryOrder ?? 999)),
        items: categoryItems.slice().sort((x, y) => x.name.localeCompare(y.name)),
      }))
      .sort((a, b) => a.order - b.order || a.category.localeCompare(b.category));
  }, [items, q, showItemsWithoutPhotos]);

  if (grouped.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {q ? `No items match "${query}"` : emptyMessage}
      </p>
    );
  }

  const renderItem = (item: ReportGalleryItem) => {
    const isSelected = item.id === selectedItemId;
    return (
      <li key={item.id}>
        <button
          type="button"
          onClick={() => onSelectItem(item.id)}
          className={cn(
            "w-full text-left text-sm py-1.5 px-2 rounded flex items-center gap-2",
            isSelected
              ? "text-primary font-medium"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          <span
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              item.navDotClassName
                ? item.navDotClassName
                : isSelected
                  ? "bg-primary"
                  : "bg-transparent"
            )}
          />
          <span className="truncate">{item.name}</span>
        </button>
      </li>
    );
  };

  const renderCategoryItems = (categoryItems: ReportGalleryItem[]) => {
    const hasSubcategories = categoryItems.some((item) => item.navSubcategory);

    if (!hasSubcategories) {
      return <ul className="space-y-0.5 pl-4">{categoryItems.map(renderItem)}</ul>;
    }

    const bySubcategory = new Map<string, ReportGalleryItem[]>();
    for (const item of categoryItems) {
      const key = item.navSubcategory ?? "Other";
      const list = bySubcategory.get(key) ?? [];
      list.push(item);
      bySubcategory.set(key, list);
    }

    const subcategories = Array.from(bySubcategory.entries())
      .map(([subcategory, items]) => ({
        subcategory,
        order: Math.min(...items.map((item) => item.navSubcategoryOrder ?? 999)),
        items: items.slice().sort((x, y) => x.name.localeCompare(y.name)),
      }))
      .sort((a, b) => a.order - b.order || a.subcategory.localeCompare(b.subcategory));

    return (
      <div className="space-y-2 pl-4">
        {subcategories.map(({ subcategory, items }) => (
          <div key={subcategory} className="space-y-0.5">
            <div className="px-2 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {subcategory}
            </div>
            <ul className="space-y-0.5">{items.map(renderItem)}</ul>
          </div>
        ))}
      </div>
    );
  };

  const effectiveValue = q
    ? grouped.map((g) => g.category)
    : Array.from(expanded);

  return (
    <Accordion
      type="multiple"
      value={effectiveValue}
      onValueChange={(next) => {
        if (q) return;
        const incoming = new Set(next);
        const prev = expanded;
        const allKeys = new Set<string>([...incoming, ...prev]);
        for (const key of allKeys) {
          if (incoming.has(key) !== prev.has(key)) {
            onToggleCategory(key);
          }
        }
      }}
      className="space-y-1"
    >
      {grouped.map(({ category, items: categoryItems }) => {
        const totalPhotos = categoryItems.reduce((n, item) => n + item.photos.length, 0);
        return (
          <AccordionItem key={category} value={category} className="border-0">
            <div className="flex items-center gap-1 group [&>h3]:flex-1">
              <AccordionTrigger className="py-2 text-sm font-medium hover:no-underline">
                {category}
              </AccordionTrigger>
              {onDownloadCategory && (
                <button
                  type="button"
                  aria-label={`Download ${category} photos`}
                  title={`Download ${category} photos`}
                  disabled={totalPhotos === 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownloadCategory(category, categoryItems);
                  }}
                  className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Download className="h-4 w-4" />
                </button>
              )}
            </div>
            <AccordionContent>
              {renderCategoryItems(categoryItems)}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
